import React, { useMemo } from 'react';
import { Button, Divider, Select, Tag } from 'antd';
import { DownOutlined, FileTextOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';

import { isLanguageBeta } from '../LanguageConfig';
import { resolveLanguageVisualIdentity } from '../utils/languageIdentity';
import './ActiveLanguageSelector.css';

export type ActiveLanguageOption = {
  value: string;
  label: string;
};

interface ActiveLanguageSelectorProps {
  value: string;
  options: ActiveLanguageOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  sourceLanguage?: string;
  originalValue?: string;
  contextLabel?: string;
  ariaLabel?: string;
  dataTestId?: string;
  dataTourId?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onAddLanguage?: () => void;
  showAddLanguageFooter?: boolean;
  addLanguageLabel?: string;
}

const isOriginalValue = (value: string, originalValue?: string) => {
  const normalized = String(value || '').trim().toLowerCase();
  const original = String(originalValue || '').trim().toLowerCase();
  return normalized === 'original' || normalized === 'original-proofread' || (Boolean(original) && normalized === original);
};

const ActiveLanguageSelector: React.FC<ActiveLanguageSelectorProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  className,
  style,
  sourceLanguage,
  originalValue,
  contextLabel = 'Editing language',
  ariaLabel = 'Current editing language',
  dataTestId,
  dataTourId,
  showSearch = false,
  searchValue,
  onSearchChange,
  onAddLanguage,
  showAddLanguageFooter = false,
  addLanguageLabel = 'Add Language',
}) => {
  const selectedOption = useMemo(
    () => options.find(option => option.value === value),
    [options, value],
  );
  const selectedIdentity = resolveLanguageVisualIdentity(value, {
    label: selectedOption?.label,
    originalLabel: selectedOption?.label,
    sourceLanguage,
  });
  const cssVars = {
    '--active-language-accent': selectedIdentity.theme.accent,
    '--active-language-bg': selectedIdentity.theme.background,
    '--active-language-border': selectedIdentity.theme.border,
    '--active-language-text': selectedIdentity.theme.text,
  } as React.CSSProperties;

  const renderOptionLabel = (option: ActiveLanguageOption) => {
    const identity = resolveLanguageVisualIdentity(option.value, {
      label: option.label,
      originalLabel: option.label,
      sourceLanguage,
    });
    const original = isOriginalValue(option.value, originalValue);
    const Icon = original ? FileTextOutlined : GlobalOutlined;
    const displayLabel = identity.displayLanguage || option.label;
    const flagLabel = identity.countryCode && identity.countryCode !== 'HE' ? '' : identity.flagFallback;

    return (
      <span className="active-language-selector__option">
        <span
          className="active-language-selector__flag language-flag"
          data-country={identity.countryCode}
          aria-hidden="true"
        >
          {flagLabel}
        </span>
        <span className="active-language-selector__copy">
          <span className="active-language-selector__caption">
            <Icon />
            {contextLabel}
          </span>
          <span className="active-language-selector__value-text">
            {displayLabel}
            {!original && isLanguageBeta(displayLabel) ? (
              <Tag color="orange" className="active-language-selector__beta-tag">
                BETA
              </Tag>
            ) : null}
          </span>
        </span>
      </span>
    );
  };

  const popupRender = onAddLanguage
    ? (menu: React.ReactElement) => (
        <div>
          <div className="active-language-selector__add">
            <Button type="text" icon={<PlusOutlined />} onClick={onAddLanguage}>
              {addLanguageLabel}
            </Button>
          </div>
          <Divider style={{ margin: '4px 0' }} />
          {menu}
          {showAddLanguageFooter ? (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <Button
                type="text"
                icon={<PlusOutlined />}
                onClick={onAddLanguage}
                className="active-language-selector__add-footer"
              >
                Add new language...
              </Button>
            </>
          ) : null}
        </div>
      )
    : undefined;

  return (
    <div
      className={[
        'active-language-selector',
        selectedIdentity.isOriginal ? 'active-language-selector--original' : 'active-language-selector--translated',
        disabled ? 'active-language-selector--disabled' : '',
        className || '',
      ].filter(Boolean).join(' ')}
      style={{ ...cssVars, ...style }}
      data-tour-id={dataTourId}
    >
      <Select
        data-testid={dataTestId}
        aria-label={ariaLabel}
        className="active-language-selector__select"
        value={value}
        onChange={onChange}
        disabled={disabled}
        showSearch={showSearch}
        filterOption={false}
        searchValue={searchValue}
        onSearch={onSearchChange}
        onOpenChange={(open) => {
          if (!open) {
            onSearchChange?.('');
          }
        }}
        notFoundContent={null}
        popupMatchSelectWidth={false}
        classNames={{ popup: { root: 'active-language-selector-dropdown' } }}
        popupRender={popupRender}
        suffixIcon={<DownOutlined />}
      >
        {options.map(option => (
          <Select.Option key={option.value} value={option.value}>
            {renderOptionLabel(option)}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default ActiveLanguageSelector;
